import * as React from 'react';
import useI18n from 'src/hooks/useI18n';

import type {JOB_INFORMATION} from 'src/types/crm';

import {
  BodyMedium,
  SubTitleSmall,
  BodySmall,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {
  Card,
  CardHeader,
  CardTitle,
  CardActions,
  CardContent,
} from '@spaced-out/ui-design-system/lib/components/Card';
import {Link} from '@spaced-out/ui-design-system/lib/components/Link';
import {Chip} from '@spaced-out/ui-design-system/lib/components/Chip';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';

import {
  Table,
  BasicSingleCell,
} from '@spaced-out/ui-design-system/lib/components/Table';

import {useSelector, useDispatch} from 'react-redux';
import {selectJobInformation, selectLocations} from 'src/selectors/crm';
import CollapsibleChipList from 'src/components/crm/lib/components/collapsible-chip-list/collapsible-chip-list';
import {getJobInformation} from 'src/action-creators/crm';

import css from './skills-tab.css';


export const SkillsTab = ({jobId}: {jobId?: string}) => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const jobInfo = useSelector(selectJobInformation);
  const dispatch = useDispatch();

  React.useEffect(() => {
    if (jobId) {
      dispatch(getJobInformation(jobId));
    }
  }, [jobId]);

  // @ts-expect-error - TS7031 - Binding element 'skills' implicitly has an 'any' type.
  const SkillsDisplay = ({skills}) => {
    const [showAll, setShowAll] = React.useState(false);
    const displaySkills = showAll ? skills : skills.slice(0, 25);

    return showAll ? (
      <Card classNames={{wrapper: css.cardWrapper}}>
        <CardHeader className={css.cardHeader}>
          <CardTitle>
            <SubTitleSmall>
              ({skills.length}) {t('Total_Skills', 'Total Skills')}
            </SubTitleSmall>
          </CardTitle>
          <CardActions>
            <Button
              type="ghost"
              size="small"
              iconLeftName="xmark"
              onClick={() => setShowAll(!showAll)}
            />
          </CardActions>
        </CardHeader>
        <CardContent className={css.cardContainer}>
          {/* @ts-expect-error - TS7006 - Parameter 'item' implicitly has an 'any' type. | TS7006 - Parameter 'index' implicitly has an 'any' type. */}
          {displaySkills.map((item, index) => (
            <Chip key={index} semantic="secondary">
              {item}
            </Chip>
          ))}
        </CardContent>
      </Card>
    ) : (
      <div className={css.skillsWrapper}>
        {/* @ts-expect-error - TS7006 - Parameter 'item' implicitly has an 'any' type. | TS7006 - Parameter 'index' implicitly has an 'any' type. */}
        {displaySkills.map((item, index) => (
          <Chip key={index} semantic="secondary">
            {item}
          </Chip>
        ))}
        {skills.length > 25 && (
          <Link onClick={() => setShowAll(!showAll)}>
            {!showAll &&
              `${t('See_More', 'See More')} (${skills.length - 25} ${t(
                'more',
                'more',
              )})`}
          </Link>
        )}
      </div>
    );
  };

  // @ts-expect-error - TS7031 - Binding element 'data' implicitly has an 'any' type.
  const BasicTableName = ({data}) => (
    <BasicSingleCell className={css.cell}>
      <BodySmall color="secondary">{data.label}</BodySmall>
    </BasicSingleCell>
  );

  // @ts-expect-error - TS7031 - Binding element 'data' implicitly has an 'any' type.
  const Skills = ({data}) => {
    if (data.id === 'mustHaveSkills') {
      return (
        <BasicSingleCell>
          {/* @ts-expect-error - TS18048 - 'jobInfo.mandatorySkills.length' is possibly 'undefined'. */}
          {jobInfo?.mandatorySkills?.length > 0 ? (
            <CollapsibleChipList
              chipTextList={jobInfo?.mandatorySkills || []}
            />
          ) : (
            '-'
          )}
        </BasicSingleCell>
      );
    } else if (data.id === 'Goodtohave') {
      return (
        <BasicSingleCell>
          {/* @ts-expect-error - TS18048 - 'jobInfo.keySkills.length' is possibly 'undefined'. */}
          {jobInfo?.keySkills?.length > 0 ? (
            <div className={css.skillsBlock}>
              <CollapsibleChipList chipTextList={jobInfo?.keySkills || []} />
            </div>
          ) : (
            '-'
          )}
        </BasicSingleCell>
      );
    } else if (data.id === 'jobDescriptionSkills') {
      return (
        <BasicSingleCell>
          <div className={css.skillsPadding}>
            {/* @ts-expect-error - TS18048 - 'jobInfo.skills.length' is possibly 'undefined'. */}
            {jobInfo?.skills?.length > 0 ? (
              <CollapsibleChipList chipTextList={jobInfo.skills || []} />
            ) : (
              '-'
            )}
          </div>
        </BasicSingleCell>
      );
    } else {
      return <BasicSingleCell>{''}</BasicSingleCell>;
    }
  };

  const tableEntries = [
    {
      label: t('Must_Have_Skills', 'Must Have Skills'),
      value: jobInfo,
      id: 'mustHaveSkills',
    },
    {
      label: t('Good_to_have_Skills', 'Good to have Skills'),
      value: jobInfo,
      id: 'Goodtohave',
    },
    {
      label: t(
        'Skills_extracted_from_Job_Description',
        'Skills extracted from Job Description',
      ),
      value: jobInfo,
      id: 'jobDescriptionSkills',
    },
  ].filter(Boolean);

  return (
    <Table
      borderRadius="12px"
      entries={tableEntries}
      headers={[
        {
          label: t('Job_Attribute', 'Job Attribute'),
          key: 'label',
          sortable: false,
          className: css.smallColumn,
          render: BasicTableName,
        },
        {
          label: t('Details', 'Details'),
          key: 'value',
          sortable: false,
          render: Skills,
        },
      ]}
    />
  );
};
